import { useState } from 'react';
import { Button, Select, Modal, ConfirmDialog } from '@/shared/ui';
import { useAgencyLocations } from '../hooks/useAgencyLocations';
import { useRunCharting } from '../hooks/useRunCharting';
import type { ChartingSubscriberInput } from '../api/payersApi';
import styles from './RunChartingSection.module.css';

// Shown pre-filled in the "Add Subscriber Info" modal — same subscriber_*
// field shape as backend/scripts/fixtures/verification-patient.example.json,
// minus codes_json (that's verification-call-specific). Purely a starting
// point for the agency to paste over with a real patient's info.
const PLACEHOLDER_SUBSCRIBER_JSON = `{
  "subscriber_first": "Alex",
  "subscriber_last": "Morgan",
  "subscriber_dob_spoken": "March 4th, 1990",
  "subscriber_dob_digits": "03041990",
  "subscriber_id": "DD12345678",
  "subscriber_id_digits": "12345678",
  "subscriber_zip": "94582",
  "subscriber_name": "Alex Morgan",
  "subscriber_relationship": "self",
  "group_number": "GRP-0001"
}`;

function parseSubscriberJson(raw: string): ChartingSubscriberInput | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const parsed: unknown = JSON.parse(trimmed);
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error('Must be a JSON object of field/value pairs');
  }
  return Object.fromEntries(
    Object.entries(parsed as Record<string, unknown>).map(([k, v]) => [k, String(v)]),
  );
}

// Agency-only — places a real outbound charting call for this payer using
// the chosen location's own credentials. Deliberately not gated on the
// location having added this payer first: charting only needs a location
// with real provider/TIN credentials on file, not a location/payer
// attachment record (see backend triggerChartingManually).
//
// Some payers' IVRs won't proceed past an "enter the patient's member
// ID/DOB" gate without real subscriber identity. There's no persisted
// patient roster yet (deliberately deferred — needs more data modeling), so
// the agency pastes it in as JSON here; it's never stored, only forwarded
// once into the outbound call's dynamic variables. Adding it is optional —
// the button only requires a location, since not every payer needs it.
export function RunChartingSection({ payerId }: { payerId: string }) {
  const { locations, isLoading: locationsLoading } = useAgencyLocations();
  const trigger = useRunCharting(payerId);
  const [locationId, setLocationId] = useState('');
  const [subscriber, setSubscriber] = useState<ChartingSubscriberInput | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [subscriberModalOpen, setSubscriberModalOpen] = useState(false);
  const [subscriberDraft, setSubscriberDraft] = useState(PLACEHOLDER_SUBSCRIBER_JSON);
  const [subscriberError, setSubscriberError] = useState<string | null>(null);

  const selectedLocation = locations.find((l) => l.id === locationId);
  const canRun = !!locationId;

  if (locationsLoading) return null;

  if (locations.length === 0) {
    return <p className={styles.hint}>No client locations found.</p>;
  }

  function openSubscriberModal() {
    setSubscriberDraft(
      subscriber ? JSON.stringify(subscriber, null, 2) : PLACEHOLDER_SUBSCRIBER_JSON,
    );
    setSubscriberError(null);
    setSubscriberModalOpen(true);
  }

  function saveSubscriberJson() {
    try {
      setSubscriber(parseSubscriberJson(subscriberDraft));
      setSubscriberError(null);
      setSubscriberModalOpen(false);
    } catch {
      setSubscriberError('That’s not valid JSON — check for a stray/missing comma or brace.');
    }
  }

  return (
    <>
      <div className={styles.actionsRow}>
        <Select
          className={styles.locationSelect}
          options={locations.map((l) => ({ value: l.id, label: `${l.name} (${l.practiceName})` }))}
          placeholder="Choose a location…"
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
        />
        <span className={styles.subscriberLink} onClick={openSubscriberModal}>
          {subscriber ? '✓ Subscriber info added (edit)' : 'Add Subscriber Info'}
        </span>
        <Button
          size="sm"
          disabled={!canRun}
          isLoading={trigger.isPending}
          onClick={() => setConfirming(true)}
        >
          Run charting call
        </Button>
      </div>

      <Modal
        isOpen={subscriberModalOpen}
        onClose={() => setSubscriberModalOpen(false)}
        title="Add Subscriber Info"
        footer={
          <div className={styles.modalActions}>
            <Button variant="tertiary" size="sm" onClick={() => setSubscriberModalOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={saveSubscriberJson}>
              Save
            </Button>
          </div>
        }
      >
        <p className={styles.hint}>
          Paste the patient's subscriber info as JSON — only needed for payers whose IVR asks for
          it. This is never saved, just used for this one call.
        </p>
        <textarea
          className={styles.jsonInput}
          spellCheck={false}
          rows={12}
          value={subscriberDraft}
          onChange={(e) => setSubscriberDraft(e.target.value)}
        />
        {subscriberError && <p className={styles.jsonError}>{subscriberError}</p>}
      </Modal>

      <ConfirmDialog
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          trigger.mutate(
            { locationId, subscriber: subscriber ?? undefined },
            {
              onSuccess: () => {
                setConfirming(false);
                setSubscriber(null);
              },
            },
          );
        }}
        title="Place a real outbound call?"
        message={
          selectedLocation
            ? `This places a real phone call using "${selectedLocation.name}"'s credentials${
                subscriber ? ', including the subscriber info you added' : ''
              }. It doesn't check whether this payer already has charting history.`
            : ''
        }
        confirmLabel="Place call"
        variant="destructive"
        isLoading={trigger.isPending}
      />
    </>
  );
}
